import {
    Toast,
    MultiToasts,
    TOAST_POSITIONS,
    TOAST_VARIANTS,
    TOAST_VISIBILITY_DURATIONS,
    BUTTON_VARIANTS,
    BUTTON_ICON_COLORS,
    StatusIndicator,
    STATUS_INDICATOR_VARIANTS,
  } from 'cdk-radial';

import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import {useSSE} from './SSE';
import  {useNotificationStore} from '../notification/NotificationStore';
import './reciever.css';
import { useTheme } from "../theme/useTheme";
import { useOktaAuth } from '@okta/okta-react';
import { MESSAGES } from '../message-provider/MessageProvider';
import { useChannelMessage } from '../channel-message/ChannelMessagingProvider';
import userProfileStore from '../core/UserProfileStore';
import { useAdoptEaseStore } from '../adopt-ease/useAdoptEaseStore';
import { INeverUsedApp } from '../../types';
import { INotification } from '../notification/NotificationInterface';
import { NotificationDetail } from '../notification/NotificationDetail';
import { useUpdateNotification } from '../notification/useNotification';
import { useSideSheetStore } from '../components/SideSheet/useSideSheetStore';
import { useWebViewStore } from '../webview/useWebViewStore';
import { getLocalizationValues } from '../utils/utils';
import { useAnalytics } from '../third-party-integration/amplitude';
import { AMPLITUDE_EVENTS } from '../../constants';
import { useApplicationStore } from '../home/ApplicationStore';
import { ProductContentIcon, StyledDefaultCDKIconText } from '../notification/styles';

interface ToastContentData {
  apps?: string[];
  workflows?: string[];
}

const TOAST_TYPE_TAGS: Record<string, { labelKey: string; defaultLabel: string; variant: string }> = {
  outage: { labelKey: 'notification.type.alert', defaultLabel: 'Alert', variant: STATUS_INDICATOR_VARIANTS.NEGATIVE },
  release: { labelKey: 'notification.type.feature', defaultLabel: 'Feature', variant: STATUS_INDICATOR_VARIANTS.INFORMATION },
};
const DEFAULT_TOAST_TYPE_TAG = { labelKey: 'notification.type.activity', defaultLabel: 'Activity', variant: STATUS_INDICATOR_VARIANTS.POSITIVE };

const ToastTypeTag = ({ type }: { type?: string }) => {
  const { t } = useTranslation();
  const tag = (type && TOAST_TYPE_TAGS[type]) || DEFAULT_TOAST_TYPE_TAG;
  return <StatusIndicator label={t(tag.labelKey, { defaultValue: tag.defaultLabel }) as string} variant={tag.variant} />;
};

const NewNotificationToastContent = ({ notification, locale, applications }: { notification: INotification; locale: string; applications: any[] }) => {
  const localizedValues = getLocalizationValues(locale, notification);
  const title = localizedValues.title || notification.title;
  const description = localizedValues.subject || notification.subject;
  const appIconColor = applications.find((app: any) => app.name === notification.application)?.icon;
  const appIconText = applications.find((app: any) => app.name === notification.application)?.iconText;
  const defaultCDKIconText = (
    <StyledDefaultCDKIconText fontSize="15px"> {'CDK'} </StyledDefaultCDKIconText>
  );
  const applicationNames = (notification.application || '').split(',').map((s) => s.trim()).filter(Boolean);
  const singleApplicationName = applicationNames.length === 1 ? applicationNames[0] : null;
  return (
    <div style={{ fontSize: '14px', lineHeight: '1.6', width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', width: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
          <ProductContentIcon iconColor={appIconColor} data-testid="app-icon">
            {appIconText || defaultCDKIconText}
          </ProductContentIcon>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600 }}>{title}</div>
            {singleApplicationName && (
              <div style={{ fontSize: '11px', marginTop: '2px' }}>
                {singleApplicationName}
              </div>
            )}
          </div>
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <ToastTypeTag type={notification.type} />
        </div>
      </div>
      {description && (
        <div
          style={{
            fontWeight: 400,
            fontSize: '13px',
            marginTop: '4px',
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
            overflow: 'hidden',
          }}
        >
          {description}
        </div>
      )}
    </div>
  );
};

const BulletListContent = ({ data }: { data: ToastContentData }) => (
  <div style={{ fontSize: '14px', lineHeight: '1.6' }}>
    {data.apps && data.apps.length > 0 && (
      <>
        <strong>New Applications:</strong>
        <ul style={{ margin: '4px 0 8px 0', paddingLeft: '20px', listStyleType: 'disc' }}>
          {data.apps.map((app, i) => (
            <li key={i}>{app}</li>
          ))}
        </ul>
      </>
    )}
    {data.workflows && data.workflows.length > 0 && (
      <>
        <strong>New Workflows:</strong>
        <ul style={{ margin: '4px 0 0 0', paddingLeft: '20px', listStyleType: 'disc' }}>
          {data.workflows.map((wf, i) => (
            <li key={i}>{wf}</li>
          ))}
        </ul>
      </>
    )}
  </div>
);

export const SSENotification = () => {

  const [event, setEvent] = useState();
  const { authState } = useOktaAuth();
  const {currentStoreId,enterpriseId} = userProfileStore();

  const token = authState?.accessToken?.accessToken || '';

  useSSE(token,currentStoreId,enterpriseId,(event:any) => setEvent(event));

  return (
    <NotificationReciever event={event}/>
  );

}

export const NotificationReciever = (props : {event:any}) => {

  const { event } = props;
  const [showToast, setShowToast] = useState(false);
  const [showToastMsg, setShowToastMsg] = useState<string>('');
  const [toastContentData, setToastContentData] = useState<ToastContentData | null>(null);
  const [newNotifications, setNewNotifications] = useState<INotification[]>([]);
  const [newNotificationToastMaxHeight, setNewNotificationToastMaxHeight] = useState<number | undefined>(undefined);
  const newNotificationToastStackRef = useRef<HTMLDivElement>(null);
  const isFirstMessagesEventRef = useRef(true);
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const updateNotifications= useNotificationStore((state:any) => state.setNotifications);
  const read = useNotificationStore((state:any) => state.read);
  const storeNotifications = useNotificationStore((state:any) => state.notifications);
  const { darkMode } = useTheme();
  const { notifyMessage } = useChannelMessage();
  const { openSideSheet, closeSideSheet, isOpen: isSideSheetOpen, extendedWidth: isSideSheetExtended } = useSideSheetStore();
  const { setCustomWebView } = useWebViewStore();
  const applications = useApplicationStore((state:any) => state.applications);
  const { mutate: updateNotification } = useUpdateNotification();
  const { locale } = userProfileStore();
  const { trackEvent } = useAnalytics();

  const {setAdoptEaseApps,setIAdoptEaseWorkflows} = useAdoptEaseStore();

  useEffect(()=>{
    try {
      if (event ){
        getNotificationUIMap().get(event.type)(event);
        notifyMessage(MESSAGES.NOTIFICATION_RECEIVED, { ...event } );
      } 
    } catch (e) {
      console.error(e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[event]);

  useEffect(() => {
    setNewNotifications((prev) => {
      if (prev.length === 0) {
        return prev;
      }
      const byId = new Map<string | undefined, INotification>(
        storeNotifications.map((n: INotification) => [n.messageId, n])
      );
      const filtered = prev.filter((n) => {
        const current = byId.get(n.messageId);
        // Drop the toast once its notification is gone (deleted/expired) or has
        // been read — e.g. the user opened it directly from the side sheet.
        return !!current && !current.deleted && !current.read;
      });
      return filtered.length === prev.length ? prev : filtered;
    });
  }, [storeNotifications]);

  useEffect(() => {
    const MAX_VISIBLE_TOASTS = 4;
    const TOAST_STACK_GAP = 12;
    const container = newNotificationToastStackRef.current;
    if (!container || newNotifications.length <= MAX_VISIBLE_TOASTS) {
      setNewNotificationToastMaxHeight(undefined);
      return;
    }
    const items = Array.from(container.children) as HTMLElement[];
    const visible = items.slice(0, MAX_VISIBLE_TOASTS);
    const height =
      visible.reduce((sum, el) => sum + el.offsetHeight, 0) +
      TOAST_STACK_GAP * (visible.length - 1);
    setNewNotificationToastMaxHeight(height || undefined);
  }, [newNotifications]);

  const parseNames = (nameStr: string): string[] => {
    return nameStr.split(',').map((s: string) => s.trim()).filter(Boolean);
  };

  const dismissNewNotification = (messageId?: string) => {
    setNewNotifications((prev) => prev.filter((n) => n.messageId !== messageId));
  };

  const handleDismissToastNotification = (notification: INotification) => {
    trackEvent(AMPLITUDE_EVENTS.NOTIFICATION_DETAIL, {
      Title: notification?.title,
      Type: notification?.type,
      Action: 'Dismiss',
    });
    dismissNewNotification(notification.messageId);
  };

  const openNewNotification = (notification: INotification) => {
    if (!notification.read) {
      updateNotification({ notification, read: true });
      read(notification, true);
    }
    if (notification.ctaUrl) {
      const view = { url: notification.ctaUrl };
      setCustomWebView(view);
      closeSideSheet();
    } else {
      openSideSheet({ body: <NotificationDetail notification={notification} /> });
    }
    trackEvent(AMPLITUDE_EVENTS.NOTIFICATION_DETAIL, {
      Title: notification?.title,
      Type: notification?.type,
      Action: 'Open',
    });
    trackEvent(AMPLITUDE_EVENTS.NOTIFICATION_DETAIL, {
      Title: notification?.title,
      Type: notification?.type,
      Action: 'Open From Toast',
    });
    dismissNewNotification(notification.messageId);
  };

  const getNotificationUIMap = () =>{
    const map = new Map();
    map.set("messages",(event:any)=>{
      const incoming: INotification[] = event.response || [];
      if (isFirstMessagesEventRef.current) {
        isFirstMessagesEventRef.current = false;
      } else {
        const existingIds = new Set(
          useNotificationStore.getState().notifications.map((n: INotification) => n.messageId)
        );
        const newlyAdded = incoming.filter(
          (n: INotification) => n.messageId && !existingIds.has(n.messageId) && !n.deleted
        );
        if (newlyAdded.length > 0) {
          setNewNotifications((prev) => [...prev, ...newlyAdded]);
        }
      }
      updateNotifications([...incoming]);
    });
    map.set("adoptease-applications",(event:any)=>{
      const neverUsedApps: INeverUsedApp[] = JSON.parse(event.message);
      setAdoptEaseApps([...neverUsedApps]);
    });
    map.set("adoptease-drive",(event:any)=>{
      const adoptEaseWorkflows = JSON.parse(event.message);
      setIAdoptEaseWorkflows([...adoptEaseWorkflows]);
    });
    map.set("profile",()=>{
      setShowToastMsg(t('home.userProfileCreated') as string);
      setToastContentData(null);
      setShowToast(true);
    });
    map.set("workflowonly", (event: any) => {
      const workflowNames = event?.message;
      if (workflowNames) {
        setToastContentData({ workflows: parseNames(workflowNames) });
      } else {
        setToastContentData(null);
        setShowToastMsg(String(t('home.notificationsNewWflow')));
      }
      setShowToast(true);
    });
    map.set("apponly", (event: any) => {
      const appNames = event?.message;
      if (appNames) {
        setToastContentData({ apps: parseNames(appNames) });
      } else {
        setToastContentData(null);
        setShowToastMsg(String(t('home.notificationsNewApp')));
      }
      setShowToast(true);
    });
    map.set("appandworkflow", (event: any) => {
      const message = event?.message;
      if (message) {
        const appsMatch = message.match(/Apps:\s*(.*?)(?:,?\s*Workflows:|$)/);
        const workflowsMatch = message.match(/Workflows:\s*(.*)/);
        const apps = appsMatch?.[1] ? parseNames(appsMatch[1]) : [];
        const workflows = workflowsMatch?.[1] ? parseNames(workflowsMatch[1]) : [];
        setToastContentData({ apps, workflows });
      } else {
        setToastContentData(null);
        setShowToastMsg(String(t('home.notificationsNewAppAndWflow')));
      }
      setShowToast(true);
    });
    map.set("appandworkflowremove",()=>{
    });
    map.set("connected",()=>{
    });
    
    return map;
  }
 
    const newNotificationToastClassName = darkMode ? 'new-notification-toast-dark' : 'new-notification-toast-light';
    const toastRightOffset = isSideSheetOpen ? (isSideSheetExtended ? 864 : 444) : 8;

    return(
        <>
          {newNotifications.length > 1 && (
              <button
                type="button"
                onClick={() => setNewNotifications([])}
                style={{
                  position: 'fixed',
                  top: '78px',
                  right: `${toastRightOffset + 372 + 16}px`,
                  zIndex: 1004,
                  background: darkMode ? '#1A1A1A' : '#fff',
                  color: darkMode ? '#fff' : '#000',
                  border: '1px solid #CCCCCC',
                  borderRadius: '16px',
                  padding: '4px 12px',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  boxShadow: darkMode
                    ? '0 3px 12px 0 rgba(255,255,255,0.18)'
                    : '0 3px 12px 0 rgba(0,0,0,0.35)',
                }}
              >
                {t('home.notificationsDismissAll', { count: newNotifications.length })}
              </button>
          )}
          {newNotifications.length > 0 && (
              <MultiToasts
              ref={newNotificationToastStackRef}
              id={`${newNotificationToastClassName}-stack`}
              className={newNotificationToastClassName}
              position={TOAST_POSITIONS.FIXED}
              style={{
                  marginTop: '70px',
                  zIndex: 1003,
                  right: `${toastRightOffset}px`,
                  ...(newNotificationToastMaxHeight
                    ? { maxHeight: `${newNotificationToastMaxHeight}px`, overflowY: 'auto' as const }
                    : {}),
                  justifyContent: 'flex-start',
              }}
              items={newNotifications.map((notification) => ({
                id: `new-notification-toast-${notification.messageId}`,
                datatestid: 'new-notification-toast',
                variant: TOAST_VARIANTS.NEUTRAL,
                visibilityDuration: TOAST_VISIBILITY_DURATIONS.SEVEN_SECONDS,
                onVisibilityDurationEnd: () => dismissNewNotification(notification.messageId),
                actions: [
                  {
                    onClick: () => handleDismissToastNotification(notification),
                    variant: BUTTON_VARIANTS.TEXT,
                    iconColor: BUTTON_ICON_COLORS.GRAY,
                    text: t('home.notificationsDismiss'),
                    style: darkMode ? {color: 'white'} : {color: 'black'}
                    },
                    {
                    onClick: () => openNewNotification(notification),
                    variant: BUTTON_VARIANTS.SECONDARY,
                    text: t('home.notificationsOpen'),
                    style: darkMode ? {color: 'black', border: '1px solid white', background: 'white'} : {background : 'black', border: '1px solid black', color: 'white'}
                    },
                ],
                content: <NewNotificationToastContent notification={notification} locale={locale} applications={applications} />,
              }))}
              />
          )}
          {showToast && (
              <Toast
              id={darkMode  ? "notification-toast-dark" : "notification-toast-light"}
              datatestid="notification-toast-dark"
              position={TOAST_POSITIONS.FIXED}
              style={{
                  marginTop: '70px',
              }}
              variant={TOAST_VARIANTS.NEUTRAL}
              visibilityDuration={TOAST_VISIBILITY_DURATIONS.SIX_SECONDS}
              onVisibilityDurationEnd={() => {
                  setShowToast(false);
                  setToastContentData(null);
              }}
              actions={[
                {
                  onClick: () => {
                      setShowToast(false);
                      setToastContentData(null);
                  },
                  variant: BUTTON_VARIANTS.TEXT,
                  iconColor: BUTTON_ICON_COLORS.GRAY,
                  text: 'Dismiss',
                  style: darkMode ? {color: 'white'} : {color: 'black'}
                  },
                  {
                  onClick: () => {
                      queryClient.invalidateQueries('getApplications');
                      queryClient.invalidateQueries('notification');
                      setShowToast(false);
                      setToastContentData(null);
                  },
                  variant: BUTTON_VARIANTS.SECONDARY,
                  text: t('home.notificationsRefresh'),
                  style: darkMode ? {color: 'black', border: '1px solid white', background: 'white'} : {background : 'black', border: '1px solid black', color: 'white'}
                  },
              ]}
              content={toastContentData
                  ? <BulletListContent data={toastContentData} />
                  : showToastMsg
                }
              />
          )}
      </>
    );
}